/**
 * Booking reference formatting.
 *
 * The database stores a per-shop sequential integer `booking_number`
 * (see supabase/add-booking-counter.sql). The user-facing reference
 * combines shop initials + booking date + zero-padded number:
 *
 *     SAS-260215-#0142
 *     └┬─┘ └──┬──┘ └─┬─┘
 *      │     │     └── booking_number, min 4 digits
 *      │     └──────── YYMMDD of starts_at
 *      └────────────── first letters of each word in shop.name (max 3)
 *
 * If either the shop name or booking_number is unavailable, we degrade
 * gracefully by falling back to the last 8 chars of the UUID.
 */

const STOPWORDS = new Set(['the', 'and', 'of', 'a', 'an']);

export const shopInitials = (name) => {
  if (!name) return 'BK';
  const words = String(name)
    .replace(/&/g, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ''))
    .filter((w) => w && !STOPWORDS.has(w.toLowerCase()));
  if (!words.length) return 'BK';
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words.slice(0, 3).map((w) => w[0]).join('').toUpperCase();
};

const yymmdd = (iso) => {
  const d = new Date(iso);
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`;
};

export const formatBookingRef = ({ shopName, startsAt, bookingNumber, uuid }) => {
  if (bookingNumber != null && shopName && startsAt) {
    return `${shopInitials(shopName)}-${yymmdd(startsAt)}-#${String(bookingNumber).padStart(4, '0')}`;
  }
  // Legacy fallback — pre-migration rows or missing data.
  return (uuid || '').slice(-8).toUpperCase();
};
